import { useEffect, useMemo, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { html } from "@codemirror/lang-html";
import { xml } from "@codemirror/lang-xml";
import { css } from "@codemirror/lang-css";
import { javascript } from "@codemirror/lang-javascript";
import { oneDark } from "@codemirror/theme-one-dark";
import { EditorView } from "@codemirror/view";
import type { Extension } from "@codemirror/state";

export type SyntaxLang = "json" | "html" | "xml" | "css" | "javascript" | "plain";

export function detectLangFromContentType(contentType: string | null | undefined): SyntaxLang {
  const ct = (contentType ?? "").toLowerCase();
  if (!ct) return "plain";
  if (ct.includes("json")) return "json";
  if (ct.includes("html")) return "html";
  if (ct.includes("xml")) return "xml";
  if (ct.includes("css")) return "css";
  if (ct.includes("javascript") || ct.includes("ecmascript")) return "javascript";
  return "plain";
}

function extensionsFor(lang: SyntaxLang): Extension[] {
  const wrap = EditorView.lineWrapping;
  switch (lang) {
    case "json": return [json(), wrap];
    case "html": return [html(), wrap];
    case "xml": return [xml(), wrap];
    case "css": return [css(), wrap];
    case "javascript": return [javascript(), wrap];
    default: return [wrap];
  }
}

interface Props {
  value: string;
  lang: SyntaxLang;
  /** Optional max-height for the viewer (defaults to 100% of parent). */
  maxHeight?: string;
}

// Read-only CodeMirror with syntax highlighting + line numbers. Used for
// formatted response bodies. Theme follows the app's dark/light toggle.
export default function CodeViewer({ value, lang, maxHeight }: Props) {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  const extensions = useMemo(() => extensionsFor(lang), [lang]);
  return (
    <CodeMirror
      value={value}
      readOnly
      editable={false}
      theme={isDark ? oneDark : "light"}
      extensions={extensions}
      height="100%"
      maxHeight={maxHeight}
      basicSetup={{
        lineNumbers: true,
        highlightActiveLine: false,
        bracketMatching: true,
        foldGutter: true,
        searchKeymap: false,
        autocompletion: false,
        indentOnInput: false,
        highlightActiveLineGutter: false,
      }}
      className="cm-host border border-border rounded"
    />
  );
}

// Pretty-print a JSON string if it parses; otherwise return the original
// untouched so the viewer still shows the raw bytes.
export function tryPrettyJson(s: string): string {
  try {
    const obj = JSON.parse(s);
    return JSON.stringify(obj, null, 2);
  } catch {
    return s;
  }
}
